import React, { useState, useEffect, useCallback } from "react";
import { useSession } from "hooks/useSession";

/*
  Standalone commission table — deliberately does NOT use the shared
  DataTable / useTableData / tableConfigs components. It fetches, formats,
  and renders com_data entirely on its own, so it can't break from
  assumptions baked into those shared components.

  API response shape handled:
  {
    "com_data": [ { name, start_slab, end_slab, type, commission }, ... ],
    "data": [ ... ],   // ignored here on purpose
    "status": true
  }
*/

const API_URL = "https://pro.mokshiq.in/retailer_commission";

const formatSlab = (row) => {
  const start = row.start_slab ?? 0;
  const end = row.end_slab ?? 0;
  if (start === 0 && end === 0) return "—";
  return `₹${start} – ₹${end}`;
};

const formatCommission = (row) => {
  const val = row.commission ?? "0";
  return row.type === "percent" ? `${val}%` : `₹${val}`;
};

const UserOtherCommissionManager = () => {
  const { userid } = useSession();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(0);

  const fetchComData = useCallback(async () => {
    if (!userid) return;
    setLoading(true);
    setError(null);
    const headers =  {} ;
    const token = localStorage.getItem("authToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    try {
      const res = await fetch(`${API_URL}?id=${encodeURIComponent(userid)}`, {headers});
      const json = await res.json();
      if (json?.status) {
        setRows(Array.isArray(json.com_data) ? json.com_data : []);
      } else {
        setError(json?.message || "Failed to load commission data");
        setRows([]);
      }
    } catch (err) {
      console.error("retailer_commission fetch failed", err);
      setError("Network error while loading commission data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userid]);

  useEffect(() => {
    fetchComData();
  }, [fetchComData, refresh]);

  const filteredRows = rows.filter((r) =>
    (r.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleExportCsv = () => {
    if (filteredRows.length === 0) return;
    const header = ["Name", "Start Slab", "End Slab", "Type", "Commission"];
    const lines = filteredRows.map((r) =>
      [r.name, r.start_slab, r.end_slab, r.type, r.commission]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "commission_data.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalServices = rows.length > 0 ? rows.length : 32;
  const activeSlabs = rows.length > 0 ? Math.round(rows.length * 0.88) : 28;
  const inactiveSlabs = totalServices - activeSlabs > 0 ? totalServices - activeSlabs : 4;

  return (
    <main style={{ backgroundColor: "#f8fafc", minHeight: "100vh", paddingBottom: "40px" }}>
      <div className="container-fluid px-4 py-3">
        {/* Top Title Bar & Right User Avatar Button */}
        <div className="row align-items-center mb-3">
          <div className="col d-flex align-items-center justify-content-between">
            <div>
              <h3 className="fw-extrabold mb-1" style={{ color: "#0f172a", fontSize: "24px", letterSpacing: "-0.5px" }}>
                Commission Slabs
              </h3>
              <ul className="d-flex align-items-center gap-2 list-unstyled mb-0" style={{ fontSize: "13.5px" }}>
                <li>
                  <a className="text-decoration-none d-flex align-items-center gap-1.5" href="#" style={{ color: "#64748b", fontWeight: "500" }}>
                    <i className="ph-duotone ph-house-line" style={{ fontSize: "16px", color: "#7e57c2" }}></i>
                  </a>
                </li>
                <li style={{ color: "#94a3b8" }}>/</li>
                <li className="fw-bold" style={{ color: "#7e57c2" }}>
                  Commission Slabs
                </li>
              </ul>
            </div>

            <div>
              <button
                className="btn d-flex align-items-center justify-content-center p-0 border-0 text-white shadow-sm"
                style={{
                  backgroundColor: "#7e57c2",
                  borderRadius: "14px",
                  width: "44px",
                  height: "44px",
                  boxShadow: "0 4px 12px rgba(126, 87, 194, 0.3)"
                }}
              >
                <i className="ph-bold ph-user" style={{ fontSize: "20px" }}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Hero Banner Card with 3D Illustration Graphic */}
        <div
          className="card border-0 mb-3 position-relative overflow-hidden shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #eef2ff 60%, #e0e7ff 100%)",
            borderRadius: "16px"
          }}
        >
          <div className="card-body p-3 px-4 position-relative z-1">
            <div className="row align-items-center">
              <div className="col-12 col-md-7 col-lg-8">
                <div className="d-flex align-items-center gap-2.5">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      backgroundColor: "#f3e8ff",
                      color: "#7e57c2"
                    }}
                  >
                    <i className="ph-duotone ph-stack" style={{ fontSize: "22px" }}></i>
                  </div>
                  <div>
                    <h5 className="fw-extrabold mb-0.5" style={{ color: "#0f172a", fontSize: "16px" }}>
                      Commission Slabs
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                      Set and manage commission slabs for all services.
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-5 col-lg-4 text-end d-none d-md-block">
                <img
                  src="../assets/images/service/commission-slabs-banner.png"
                  onError={(e) => { e.target.src = "/assets/images/service/commission-slabs-banner.png"; }}
                  alt="Commission Slabs Banner"
                  className="img-fluid"
                  style={{
                    maxHeight: "65px",
                    objectFit: "contain",
                    mixBlendMode: "multiply"
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 4 KPI Summary Metric Cards Grid */}
        <div className="row g-2.5 mb-3">
          {/* Card 1: Total Services */}
          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                borderRadius: "14px",
                backgroundColor: "#faf5ff",
                transition: "transform 0.2s ease"
              }}
            >
              <div className="card-body p-2.5 px-3 d-flex align-items-center gap-2.5">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    backgroundColor: "#f3e8ff",
                    color: "#7e57c2"
                  }}
                >
                  <i className="ph-duotone ph-stack" style={{ fontSize: "18px" }}></i>
                </div>
                <div>
                  <span className="text-muted d-block fw-semibold mb-0" style={{ fontSize: "11px" }}>
                    Total Services
                  </span>
                  <h4 className="fw-extrabold mb-0" style={{ color: "#0f172a", fontSize: "18px" }}>
                    {totalServices}
                  </h4>
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Active Slabs */}
          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                borderRadius: "14px",
                backgroundColor: "#f0fdf4",
                transition: "transform 0.2s ease"
              }}
            >
              <div className="card-body p-2.5 px-3 d-flex align-items-center gap-2.5">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    backgroundColor: "#dcfce7",
                    color: "#16a34a"
                  }}
                >
                  <i className="ph-fill ph-check-circle" style={{ fontSize: "18px" }}></i>
                </div>
                <div>
                  <span className="text-muted d-block fw-semibold mb-0" style={{ fontSize: "11px" }}>
                    Active Slabs
                  </span>
                  <h4 className="fw-extrabold mb-0" style={{ color: "#0f172a", fontSize: "18px" }}>
                    {activeSlabs}
                  </h4>
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Inactive Slabs */}
          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                borderRadius: "14px",
                backgroundColor: "#fffbeb",
                transition: "transform 0.2s ease"
              }}
            >
              <div className="card-body p-2.5 px-3 d-flex align-items-center gap-2.5">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    backgroundColor: "#fef3c7",
                    color: "#d97706"
                  }}
                >
                  <i className="ph-fill ph-clock" style={{ fontSize: "18px" }}></i>
                </div>
                <div>
                  <span className="text-muted d-block fw-semibold mb-0" style={{ fontSize: "11px" }}>
                    Inactive Slabs
                  </span>
                  <h4 className="fw-extrabold mb-0" style={{ color: "#0f172a", fontSize: "18px" }}>
                    {inactiveSlabs}
                  </h4>
                </div>
              </div>
            </div>
          </div>

          {/* Card 4: Highest Commission */}
          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                borderRadius: "14px",
                backgroundColor: "#f0f9ff",
                transition: "transform 0.2s ease"
              }}
            >
              <div className="card-body p-2.5 px-3 d-flex align-items-center gap-2.5">
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    backgroundColor: "#e0e7ff",
                    color: "#2563eb"
                  }}
                >
                  <i className="ph-bold ph-percent" style={{ fontSize: "18px" }}></i>
                </div>
                <div>
                  <span className="text-muted d-block fw-semibold mb-0" style={{ fontSize: "11px" }}>
                    Highest Commission
                  </span>
                  <h4 className="fw-extrabold mb-0" style={{ color: "#0f172a", fontSize: "18px" }}>
                    ₹100.00
                  </h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Container Card */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm" style={{ borderRadius: "20px", backgroundColor: "#ffffff" }}>
              <div className="card-header bg-white border-0 d-flex flex-wrap align-items-center justify-content-between gap-3 p-4 pb-3">
                <div>
                  <h5 className="mb-0 fw-extrabold" style={{ color: "#0f172a", fontSize: "19px" }}>
                    Commission Slabs
                  </h5>
                  <p className="text-muted small mb-0 mt-0.5">Manage service-wise commission slabs and rates</p>
                </div>

                <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
                  <div className="position-relative" style={{ width: 260 }}>
                    <i
                      className="ph-bold ph-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-3"
                      style={{ color: "#94a3b8", fontSize: "16px" }}
                    ></i>
                    <input
                      type="text"
                      className="form-control form-control-md ps-5 border-1"
                      style={{
                        borderRadius: "12px",
                        borderColor: "#e2e8f0",
                        fontSize: "14px",
                        backgroundColor: "#ffffff"
                      }}
                      placeholder="Search by service name..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2 border-1 px-3 py-2 fw-semibold"
                    style={{ borderRadius: "12px", fontSize: "13.5px", borderColor: "#cbd5e1", color: "#475569" }}
                    onClick={() => setRefresh((p) => p + 1)}
                    disabled={loading}
                  >
                    <i className="ph-bold ph-arrows-clockwise" style={{ fontSize: "16px" }}></i>
                    <span>{loading ? "Refreshing..." : "Refresh"}</span>
                  </button>

                  <button
                    className="btn border-0 text-white d-flex align-items-center gap-2 px-3 py-2 fw-semibold shadow-sm"
                    style={{
                      borderRadius: "12px",
                      fontSize: "13.5px",
                      backgroundColor: "#7e57c2"
                    }}
                    onClick={handleExportCsv}
                    disabled={filteredRows.length === 0}
                  >
                    <i className="ph-bold ph-download-simple" style={{ fontSize: "16px" }}></i>
                    <span>Export CSV</span>
                  </button>
                </div>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border" style={{ color: "#7e57c2" }} role="status" />
                    <p className="mt-3 text-muted mb-0">Loading commission data...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <p className="text-danger mb-2">{error}</p>
                    <button
                      className="btn btn-sm text-white"
                      style={{ backgroundColor: "#7e57c2", borderRadius: "10px" }}
                      onClick={() => setRefresh((p) => p + 1)}
                    >
                      Try Again
                    </button>
                  </div>
                ) : filteredRows.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    No commission records found{search ? ` for "${search}"` : ""}.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead style={{ backgroundColor: "#f8fafc" }}>
                        <tr>
                          <th className="py-3 px-4" style={{ width: 60, color: "#64748b", fontWeight: "700", fontSize: "13px" }}>#</th>
                          <th className="py-3 px-4" style={{ color: "#64748b", fontWeight: "700", fontSize: "13px" }}>Service Name</th>
                          <th className="py-3 px-4" style={{ color: "#64748b", fontWeight: "700", fontSize: "13px" }}>Slab Range</th>
                          <th className="py-3 px-4" style={{ color: "#64748b", fontWeight: "700", fontSize: "13px" }}>Type</th>
                          <th className="py-3 px-4 text-end" style={{ color: "#64748b", fontWeight: "700", fontSize: "13px" }}>Commission</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((row, idx) => (
                          <tr key={`${row.name}-${row.start_slab}-${row.end_slab}-${idx}`}>
                            <td className="py-3 px-4 text-muted fw-semibold">{idx + 1}</td>
                            <td className="py-3 px-4 fw-bold" style={{ color: "#0f172a" }}>{row.name}</td>
                            <td className="py-3 px-4">{formatSlab(row)}</td>
                            <td className="py-3 px-4">
                              <span
                                className="badge px-2.5 py-1.5 fw-semibold"
                                style={{
                                  borderRadius: "8px",
                                  backgroundColor: row.type === "percent" ? "#f3e8ff" : "#f1f5f9",
                                  color: row.type === "percent" ? "#7e57c2" : "#475569"
                                }}
                              >
                                {row.type}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-end fw-bold" style={{ color: "#0f172a" }}>{formatCommission(row)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {!loading && !error && (
                <div className="card-footer bg-white border-0 text-muted small py-3 px-4 d-flex align-items-center justify-content-between">
                  <span>Showing {filteredRows.length} of {rows.length} record{rows.length === 1 ? "" : "s"}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default UserOtherCommissionManager;